import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, ArrowLeft } from "lucide-react";
import "@/App.css";

const MCP_ENDPOINT = "https://mcp.phsinc.com/api/mcp";

const CodeBlock = ({ code, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error(e);
    }
  };
  return (
    <div className="endpoint-block my-4">
      <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-slate-700/60">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">{label}</span>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium text-slate-300 hover:text-white hover:bg-slate-700/60 transition-colors"
          data-testid={`copy-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
        >
          {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="overflow-x-auto px-4 py-3 font-mono text-[12.5px] leading-relaxed text-sky-200 whitespace-pre">{code}</pre>
    </div>
  );
};

const Badge = ({ type }) => (
  <span
    className={`rounded px-2 py-0.5 font-mono text-[10px] font-semibold tracking-wider ${
      type === "READ" ? "bg-emerald-50 text-emerald-700" : "bg-[#00599c]/10 text-[#00599c]"
    }`}
  >
    {type === "READ" ? "READ-ONLY · IDEMPOTENT" : "WRITE · NON-DESTRUCTIVE"}
  </span>
);

const ParamTable = ({ rows, headers }) => (
  <div className="my-4 overflow-x-auto rounded-lg border border-slate-200">
    <table className="w-full text-left text-sm">
      <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
        <tr>{headers.map((h) => <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((r, i) => (
          <tr key={i} className="bg-white">
            {r.map((c, j) => (
              <td key={j} className={`px-4 py-2.5 align-top ${j === 0 ? "font-mono text-[13px] text-slate-900" : "text-slate-600"}`}>{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PALLET_SUMMARY_FIELDS = "product_id, name, sku, pallet_type, material, length_in, width_in, height_in, weight_lb, capacity_static_lb, capacity_dynamic_lb, price_usd, quote_only, stock, availability, rackable, image, permalink";

const TOOLS_DOC = [
  {
    name: "search_pallets", type: "READ",
    summary: "Search the catalog by keyword and/or category. The gateway fetches the full product list from the PHS production API and filters in memory on name, description, pallet type and material.",
    params: [
      ["query", "string", "no", 'Keyword matched against name, description, pallet_type and material. Empty = match all.'],
      ["category", "string", "no", 'Category filter, e.g. "rackable", "nestable", "stackable", "export", "hygienic".'],
      ["max_results", "integer", "no", "Maximum results to return. Default 8."],
    ],
    returns: [
      ["results", "PalletSummary[]", `Matching pallets. Each includes: ${PALLET_SUMMARY_FIELDS}.`],
      ["count", "integer", "Number of results returned."],
    ],
    example: `{"jsonrpc": "2.0", "id": 1, "method": "tools/call",
 "params": {"name": "search_pallets",
            "arguments": {"query": "rackable", "max_results": 3}}}`,
  },
  {
    name: "get_pallet_details", type: "READ",
    summary: "Full engineering spec sheet for a single SKU — dimensions, static/dynamic load, deck type, material, color, weight, price, availability and description.",
    params: [["product_id", "string", "yes", 'PHS product id, e.g. "phs_15735".']],
    returns: [
      ["…PalletSummary", "object", "All summary fields (see search_pallets)."],
      ["color", "string", "Pallet color."],
      ["deck_type", "string", 'Deck construction, e.g. "Solid" or "Grid".'],
      ["description", "string", "Full marketing/engineering description."],
    ],
    example: `{"jsonrpc": "2.0", "id": 2, "method": "tools/call",
 "params": {"name": "get_pallet_details",
            "arguments": {"product_id": "phs_15735"}}}`,
  },
  {
    name: "popular_pallets", type: "READ",
    summary: "Real best sellers aggregated from PHS order history, plus a short buying guide mapping pallet types to use cases. The recommended starting point for new shoppers.",
    params: [["max_results", "integer", "no", "Maximum best sellers to return. Default 5."]],
    returns: [
      ["best_sellers", "PalletSummary[]", "Best-selling pallets with live specs and pricing."],
      ["buying_guide", "string", "Rackable = racking; stackable = floor stacking; nestable = return-freight savings; export = one-way, ISPM-15 exempt; hygienic = food/pharma washdown."],
    ],
    example: `{"jsonrpc": "2.0", "id": 3, "method": "tools/call",
 "params": {"name": "popular_pallets", "arguments": {"max_results": 5}}}`,
  },
  {
    name: "request_quote", type: "WRITE",
    summary: "Files a real quote request with the PHS sales team (same flow as the mobile app). PHS emails the customer a confirmation with a payment link — no payment ever happens in chat.",
    params: [
      ["contact_name", "string", "yes", "Customer's full name."],
      ["contact_email", "string", "yes", "Where the quote and payment link are emailed."],
      ["destination_zip", "string", "yes", "US ZIP code for the freight estimate."],
      ["items", "array", "yes", "[{product_id, quantity}] — one entry per pallet SKU."],
      ["company", "string", "no", "Company name."],
      ["notes", "string", "no", "Free-form notes for the sales team."],
    ],
    returns: [
      ["quote_number", "string", "PHS quote reference."],
      ["status", "string", 'Quote status, e.g. "pending".'],
      ["subtotal_usd / freight_usd / total_usd", "number", "Itemized pricing with live freight estimate."],
      ["lead_time_days", "integer", "Estimated production/shipping lead time."],
      ["view_url", "string", "Browser link to review the quote."],
      ["message", "string", "Human-readable confirmation."],
    ],
    example: `{"jsonrpc": "2.0", "id": 4, "method": "tools/call",
 "params": {"name": "request_quote",
            "arguments": {"contact_name": "Jane Doe",
                          "contact_email": "jane@acme.com",
                          "destination_zip": "75201",
                          "items": [{"product_id": "phs_15735", "quantity": 200}],
                          "company": "Acme Logistics"}}}`,
  },
  {
    name: "request_login_code", type: "WRITE",
    summary: "Starts passwordless sign-in for a returning customer: emails a single-use 6-digit code (10-minute expiry). The response is identical whether or not the account exists, preventing enumeration. Assistants must never ask for passwords.",
    params: [["email", "string", "yes", "The customer's PHS Pallets account email."]],
    returns: [
      ["ok", "boolean", "Always true for accepted requests."],
      ["message", "string", "Generic confirmation that a code was emailed if the account exists."],
    ],
    example: `{"jsonrpc": "2.0", "id": 5, "method": "tools/call",
 "params": {"name": "request_login_code",
            "arguments": {"email": "customer@example.com"}}}`,
  },
  {
    name: "login_with_code", type: "WRITE",
    summary: "Completes passwordless sign-in: exchanges the emailed 6-digit code for a bearer token used by my_orders, track_order, my_quotes and reorder. Codes are single-use with attempt limits.",
    params: [
      ["email", "string", "yes", "The account email the code was sent to."],
      ["code", "string", "yes", "The 6-digit code from the email."],
    ],
    returns: [
      ["token", "string", "Bearer token — pass as the token argument to authenticated tools."],
      ["name", "string", "The customer's display name."],
    ],
    example: `{"jsonrpc": "2.0", "id": 6, "method": "tools/call",
 "params": {"name": "login_with_code",
            "arguments": {"email": "customer@example.com", "code": "847291"}}}`,
  },
  {
    name: "my_orders", type: "READ",
    summary: "Lists the signed-in customer's orders — status, totals, dates and line items.",
    params: [["token", "string", "yes", "Bearer token from login_with_code."]],
    returns: [
      ["orders", "OrderSummary[]", "order_id, order_number, status, total_usd, created_at, items."],
      ["count", "integer", "Number of orders."],
    ],
    example: `{"jsonrpc": "2.0", "id": 7, "method": "tools/call",
 "params": {"name": "my_orders", "arguments": {"token": "<bearer token>"}}}`,
  },
  {
    name: "track_order", type: "READ",
    summary: "Live shipment tracking for one of the customer's own orders: status, carrier, tracking number, ETA and the full event timeline (confirmed → processing → shipped → in transit → out for delivery → delivered).",
    params: [
      ["order_id", "string", "yes", "The order to track (from my_orders)."],
      ["token", "string", "yes", "Bearer token from login_with_code."],
    ],
    returns: [
      ["order_id / status / carrier / tracking_number / eta", "string", "Current shipment state."],
      ["timeline", "object[]", "Chronological shipment events."],
    ],
    example: `{"jsonrpc": "2.0", "id": 8, "method": "tools/call",
 "params": {"name": "track_order",
            "arguments": {"order_id": "<order id>", "token": "<bearer token>"}}}`,
  },
  {
    name: "my_quotes", type: "READ",
    summary: "Lists the signed-in customer's quote pipeline with statuses, totals and line items.",
    params: [["token", "string", "yes", "Bearer token from login_with_code."]],
    returns: [
      ["quotes", "QuoteSummary[]", "quote_id, quote_number, status, total_usd, created_at, items."],
      ["count", "integer", "Number of quotes."],
    ],
    example: `{"jsonrpc": "2.0", "id": 9, "method": "tools/call",
 "params": {"name": "my_quotes", "arguments": {"token": "<bearer token>"}}}`,
  },
  {
    name: "reorder", type: "WRITE",
    summary: "One-step repeat purchase: creates a fresh quote from a past order (or the most recent order when order_id is omitted) at the customer's previous unit pricing with a fresh freight estimate, and emails a payment link. Strictly additive — the original order is never modified.",
    params: [
      ["token", "string", "yes", "Bearer token from login_with_code."],
      ["order_id", "string", "no", "Past order to reorder. Empty = most recent order."],
    ],
    returns: [
      ["quote_number / status / items", "mixed", "The new quote and its line items."],
      ["subtotal_usd / freight_usd / total_usd", "number", "Re-priced totals with live freight."],
      ["lead_time_days / view_url", "mixed", "Lead time and browser link."],
      ["message", "string", "Confirms a payment-link email was sent — payment never happens in chat."],
    ],
    example: `{"jsonrpc": "2.0", "id": 10, "method": "tools/call",
 "params": {"name": "reorder", "arguments": {"token": "<bearer token>"}}}`,
  },
];

const NAV = [
  { id: "introduction", label: "Introduction" },
  { id: "quick-start", label: "Quick start" },
  { id: "authentication", label: "Authentication" },
  { id: "oauth", label: "OAuth 2.1 endpoints" },
  { id: "customer-sign-in", label: "In-chat customer sign-in" },
  { id: "tools", label: "Tools reference", children: TOOLS_DOC.map((t) => t.name) },
  { id: "protocol", label: "Protocol notes" },
  { id: "errors", label: "Error handling" },
  { id: "resources", label: "Resources" },
];

const H2 = ({ id, children }) => (
  <h2 id={id} className="heading-font scroll-mt-24 border-b border-slate-200 pb-3 text-2xl font-bold tracking-tight text-slate-900">
    {children}
  </h2>
);

const P = ({ children }) => <p className="mt-4 text-[15px] leading-relaxed text-slate-600">{children}</p>;

export default function Docs() {
  return (
    <div className="page-root" data-testid="docs-page">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="docs-home-link" className="flex items-center gap-3">
              <img src="/premier-logo.png" alt="Premier Handling Solutions" className="h-8 w-auto" />
            </Link>
            <span className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-mono text-slate-500">API Docs</span>
          </div>
          <nav className="flex items-center gap-5 text-sm font-medium">
            <Link to="/" className="inline-flex items-center gap-1.5 text-slate-600 hover:text-[#00599c] transition-colors" data-testid="docs-back-link">
              <ArrowLeft size={15} /> Home
            </Link>
            <a href="https://phsinc.com" target="_blank" rel="noopener noreferrer" className="text-[#00599c] hover:text-[#00477d] transition-colors">phsinc.com ↗</a>
          </nav>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl gap-10 px-6 py-10">
        {/* Sidebar */}
        <aside className="hidden w-60 shrink-0 lg:block" data-testid="docs-sidebar">
          <nav className="sticky top-24 space-y-1 text-sm">
            {NAV.map((n) => (
              <div key={n.id}>
                <a href={`#${n.id}`} className="block rounded px-3 py-1.5 font-medium text-slate-700 hover:bg-[#00599c]/5 hover:text-[#00599c] transition-colors">
                  {n.label}
                </a>
                {n.children && (
                  <div className="ml-3 border-l border-slate-200">
                    {n.children.map((c) => (
                      <a key={c} href={`#tool-${c}`} className="block px-3 py-1 font-mono text-[12px] text-slate-500 hover:text-[#00599c] transition-colors">
                        {c}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="min-w-0 max-w-3xl flex-1 pb-24">
          <p className="heading-font text-xs font-bold uppercase tracking-[0.2em] text-[#00599c]">PHS Pallets MCP Gateway</p>
          <h1 className="heading-font mt-3 text-4xl font-bold tracking-tight text-slate-900" data-testid="docs-heading">Documentation</h1>
          <P>
            Everything you need to connect an AI assistant to the PHS Pallets catalog: endpoint details,
            authentication, and a complete reference for all 10 tools.
          </P>

          <section className="mt-12">
            <H2 id="introduction">Introduction</H2>
            <P>
              The PHS Pallets MCP Gateway exposes live PHS production data — 100+ plastic pallet SKUs,
              real quotes with freight estimates, order history and shipment tracking — to any client that
              speaks the <a className="text-[#00599c] underline underline-offset-2" href="https://modelcontextprotocol.io" target="_blank" rel="noopener noreferrer">Model Context Protocol</a> (ChatGPT, Claude, and any MCP-compatible agent).
            </P>
            <ParamTable
              headers={["Property", "Value"]}
              rows={[
                ["Endpoint", MCP_ENDPOINT],
                ["Transport", "Streamable HTTP (stateless, JSON responses)"],
                ["Wire format", "JSON-RPC 2.0 over HTTP POST"],
                ["Tools", "10 (6 read-only, 4 non-destructive writes)"],
                ["Server auth", "Optional — anonymous OAuth 2.1 available for clients that require it"],
                ["Data storage", "None — every call proxies to the PHS production API over HTTPS"],
              ]}
            />
          </section>

          <section className="mt-12">
            <H2 id="quick-start">Quick start</H2>
            <h3 className="heading-font mt-6 text-lg font-semibold text-slate-900">ChatGPT</h3>
            <P>
              Settings → Apps &amp; Connectors → Create. Set <strong>Name</strong> to PHS Pallets,{" "}
              <strong>Server URL</strong> to the endpoint below, and <strong>Authentication</strong> to No Auth.
            </P>
            <CodeBlock label="MCP Server URL" code={MCP_ENDPOINT} />
            <h3 className="heading-font mt-6 text-lg font-semibold text-slate-900">Claude</h3>
            <P>
              Settings → Connectors → Add custom connector, then paste the same URL. Claude discovers the
              gateway's OAuth server automatically and connects in one click — no client ID or secret required.
            </P>
            <h3 className="heading-font mt-6 text-lg font-semibold text-slate-900">cURL</h3>
            <CodeBlock
              label="List all tools"
              code={`curl -X POST ${MCP_ENDPOINT} \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json, text/event-stream" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list","params":{}}'`}
            />
            <CodeBlock
              label="Call a tool"
              code={`curl -X POST ${MCP_ENDPOINT} \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json, text/event-stream" \\
  -d '{"jsonrpc":"2.0","id":2,"method":"tools/call",
       "params":{"name":"popular_pallets","arguments":{"max_results":3}}}'`}
            />
          </section>

          <section className="mt-12">
            <H2 id="authentication">Authentication</H2>
            <P>
              The gateway has two independent layers. Neither ever handles passwords in chat.
            </P>
            <ParamTable
              headers={["Layer", "Who it's for", "How it works"]}
              rows={[
                ["Server access", "MCP clients (ChatGPT, Claude)", "Public — no credentials required. Clients that mandate OAuth (Claude) use the anonymous auto-approving OAuth 2.1 flow below. Issued Bearer tokens are accepted on every request."],
                ["Customer accounts", "Returning PHS customers, inside the conversation", "Passwordless one-time codes: request_login_code emails a 6-digit code, login_with_code exchanges it for a bearer token passed as a tool argument."],
              ]}
            />
          </section>

          <section className="mt-12">
            <H2 id="oauth">OAuth 2.1 endpoints</H2>
            <P>
              A spec-compliant, auto-approving authorization server for MCP clients: dynamic client
              registration (RFC 7591), PKCE S256 (RFC 7636), authorization-server metadata (RFC 8414) and
              protected-resource metadata (RFC 9728). Codes and tokens are stateless HMAC-signed values.
            </P>
            <ParamTable
              headers={["Endpoint", "Method", "Purpose"]}
              rows={[
                ["/.well-known/oauth-authorization-server", "GET", "Authorization-server discovery metadata."],
                ["/.well-known/oauth-protected-resource/api/mcp", "GET", "Protected-resource metadata for the MCP endpoint."],
                ["/api/oauth/register", "POST (JSON)", "Dynamic client registration — returns a public client_id (no secret)."],
                ["/api/oauth/authorize", "GET", "Auto-approves and 302-redirects to redirect_uri with ?code=…&state=…."],
                ["/api/oauth/token", "POST (form-encoded)", "authorization_code (PKCE S256 verified) and refresh_token grants."],
              ]}
            />
            <CodeBlock
              label="Token exchange"
              code={`curl -X POST https://mcp.phsinc.com/api/oauth/token \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -d "grant_type=authorization_code" \\
  -d "code=<code from redirect>" \\
  -d "code_verifier=<PKCE verifier>" \\
  -d "redirect_uri=<your callback>" \\
  -d "client_id=<from /api/oauth/register>"`}
            />
          </section>

          <section className="mt-12">
            <H2 id="customer-sign-in">In-chat customer sign-in</H2>
            <P>The three-step passwordless flow assistants should follow for returning customers:</P>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-[15px] text-slate-600">
              <li>Call <code className="font-mono text-[13px] text-slate-900">request_login_code</code> with the customer's account email. A single-use 6-digit code (10-minute expiry) is emailed.</li>
              <li>Ask the customer for the code from their inbox — <strong>never ask for a password</strong>.</li>
              <li>Call <code className="font-mono text-[13px] text-slate-900">login_with_code</code> to exchange it for a bearer token, then pass that token to <code className="font-mono text-[13px] text-slate-900">my_orders</code>, <code className="font-mono text-[13px] text-slate-900">track_order</code>, <code className="font-mono text-[13px] text-slate-900">my_quotes</code> and <code className="font-mono text-[13px] text-slate-900">reorder</code>.</li>
            </ol>
          </section>

          <section className="mt-12">
            <H2 id="tools">Tools reference</H2>
            <P>
              Every tool publishes a typed <code className="font-mono text-[13px]">outputSchema</code> and MCP
              ToolAnnotations. Results arrive as <code className="font-mono text-[13px]">structuredContent</code> in
              the JSON-RPC response. Writes are strictly additive — nothing can be modified or deleted through this API.
            </P>
            {TOOLS_DOC.map((t) => (
              <div key={t.name} id={`tool-${t.name}`} className="mt-10 scroll-mt-24 rounded-xl border border-slate-200 bg-white p-6" data-testid={`docs-tool-${t.name}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="heading-font font-mono text-lg font-bold text-slate-900">{t.name}</h3>
                  <Badge type={t.type} />
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{t.summary}</p>
                <h4 className="mt-5 text-xs font-bold uppercase tracking-wider text-slate-400">Parameters</h4>
                <ParamTable headers={["Name", "Type", "Required", "Description"]} rows={t.params} />
                <h4 className="mt-5 text-xs font-bold uppercase tracking-wider text-slate-400">Returns</h4>
                <ParamTable headers={["Field", "Type", "Description"]} rows={t.returns} />
                <h4 className="mt-5 text-xs font-bold uppercase tracking-wider text-slate-400">Example</h4>
                <CodeBlock label={`tools/call · ${t.name}`} code={t.example} />
              </div>
            ))}
          </section>

          <section className="mt-12">
            <H2 id="protocol">Protocol notes</H2>
            <ul className="mt-4 list-disc space-y-2.5 pl-5 text-[15px] text-slate-600">
              <li><strong>Stateless:</strong> no session or <code className="font-mono text-[13px]">initialize</code> handshake is required before <code className="font-mono text-[13px]">tools/list</code> or <code className="font-mono text-[13px]">tools/call</code>. Every request is self-contained and horizontally scalable.</li>
              <li><strong>Headers:</strong> send <code className="font-mono text-[13px]">Content-Type: application/json</code> and <code className="font-mono text-[13px]">Accept: application/json, text/event-stream</code>.</li>
              <li><strong>No redirects:</strong> <code className="font-mono text-[13px]">/api/mcp</code> and <code className="font-mono text-[13px]">/api/mcp/</code> are equivalent — the gateway rewrites the path internally so clients never see a 307.</li>
              <li><strong>Structured output:</strong> tool results include both human-readable <code className="font-mono text-[13px]">content</code> and machine-readable <code className="font-mono text-[13px]">structuredContent</code> validated against each tool's <code className="font-mono text-[13px]">outputSchema</code>.</li>
              <li><strong>Annotations:</strong> read-only tools carry <code className="font-mono text-[13px]">readOnlyHint=true, idempotentHint=true</code>; write tools carry <code className="font-mono text-[13px]">readOnlyHint=false, destructiveHint=false</code>. All tools are <code className="font-mono text-[13px]">openWorldHint=false</code> — the gateway only ever talks to the PHS first-party API.</li>
              <li><strong>Payments:</strong> never happen in chat. Quotes are paid via Stripe checkout through the emailed link or the PHS Pallets mobile app.</li>
            </ul>
          </section>

          <section className="mt-12">
            <H2 id="errors">Error handling</H2>
            <P>
              Tool failures return a normal JSON-RPC result with <code className="font-mono text-[13px]">isError: true</code> and
              a plain-text explanation — upstream API problems are surfaced as{" "}
              <code className="font-mono text-[13px]">PHS API error &lt;status&gt;: &lt;detail&gt;</code>. Invalid or expired
              sign-in codes, bad tokens and PKCE failures all produce descriptive errors rather than 5xx responses.
            </P>
            <CodeBlock
              label="Error result shape"
              code={`{"jsonrpc": "2.0", "id": 5,
 "result": {"isError": true,
            "content": [{"type": "text",
                         "text": "PHS API error 401: Invalid or expired code"}]}}`}
            />
          </section>

          <section className="mt-12">
            <H2 id="resources">Resources</H2>
            <ParamTable
              headers={["Resource", "URL"]}
              rows={[
                ["MCP endpoint", MCP_ENDPOINT],
                ["ChatGPT app submission JSON (download)", "https://mcp.phsinc.com/api/chatgpt-app-submission.json"],
                ["App icon (256×256 PNG)", "https://mcp.phsinc.com/icon-256.png"],
                ["Plastic pallet catalog", "https://www.phsinc.com/pallet/plastic-pallets/"],
                ["PHS Pallets on Google Play", "https://play.google.com/store/apps/details?id=com.emergent.pallethub.wqcb7t"],
                ["PHS Pallets on the App Store", "https://apps.apple.com/us/app/phs-pallet/id6788104215"],
                ["Support", "(800) 404-2311 · phsinc.com"],
              ]}
            />
          </section>
        </main>
      </div>

      <footer className="border-t border-slate-200 bg-slate-50">
        <p className="mx-auto max-w-7xl px-6 py-5 text-sm text-slate-500">
          © {new Date().getFullYear()} PHS Pallets · Premier Handling Solutions · MCP Gateway
        </p>
      </footer>
    </div>
  );
}
